import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SideNav } from '../navigation/SideNav';
import { TopBar } from '../navigation/TopBar';
import DiwaliFireworksCanvas from '../common/DiwaliFireworksCanvas';
import DiwaliFairyLights from '../common/DiwaliFairyLights';

interface AppShellProps {
  children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'niftysutra-sidebar-collapsed';

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Close mobile drawer whenever route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // ignore localStorage errors
      }
      return next;
    });
  };

  return (
    <div className={`ns-shell ${isCollapsed ? 'ns-shell--sidebar-collapsed' : ''}`}>
      {/* Festive Diwali Ambient Fireworks & Hanging Fairy Lights */}
      <DiwaliFireworksCanvas />
      <DiwaliFairyLights />

      {/* Desktop Left Rail & Mobile Drawer */}
      <SideNav
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onCloseMobile={() => setIsMobileOpen(false)}
      />

      {/* Mobile Backdrop Overlay */}
      <div
        className={`ns-mobile-overlay ${isMobileOpen ? 'is-visible' : ''}`}
        onClick={() => setIsMobileOpen(false)}
        aria-hidden="true"
      />

      {/* Main Content Area */}
      <div className="ns-content-wrapper">
        <TopBar
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
          onToggleMobile={() => setIsMobileOpen((prev) => !prev)}
        />

        <main id="main-content" className="ns-stage" role="main" tabIndex={-1}>
          {children}
        </main>

        <footer className="ns-footer" role="contentinfo">
          <p className="ns-footer__disclaimer">
            <strong>Disclaimer:</strong> NiftySutra is an analytical and market intelligence workspace provided strictly for research and educational purposes. It does not constitute investment or trading advice, recommendation, or endorsement of any security.
          </p>
          <div className="ns-footer__links">
            <a
              href="https://x.com/dhuperji"
              target="_blank"
              rel="noopener noreferrer"
              className="ns-footer__x-link"
              title="Connect with @dhuperji on X"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span>Follow @dhuperji</span>
            </a>
            <span className="ns-footer__dot" aria-hidden="true">•</span>
            <Link to="/guide" className="ns-footer__link" title="Trading Playbook & User Guide">
              Trading Playbook
            </Link>
            <span className="ns-footer__dot" aria-hidden="true">•</span>
            <Link to="/contact-us" className="ns-footer__link" title="Contact Us & Support">
              Contact &amp; Support
            </Link>
            <span className="ns-footer__dot" aria-hidden="true">•</span>
            <span className="ns-footer__meta">
              &copy; {new Date().getFullYear()} NiftySutra
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AppShell;
